import React, { ForwardedRef } from 'react';

import { Platform, ScrollView } from 'react-native';

import { useIsDesktopWebBreakpoint } from 'hooks/use-is-desktop-web-breakpoint';

type Props<Scrollable extends ScrollView = ScrollView> = {
  cursor?: string;
  outerRef?: ForwardedRef<Scrollable>;
};

// @see https://github.com/necolas/react-native-web/issues/2089

export function useDraggableScroll<Scrollable extends ScrollView = ScrollView>({
  outerRef,
  cursor = 'grab',
}: Props<Scrollable> = {}) {
  const isDesktop = useIsDesktopWebBreakpoint();
  const ref = React.useRef<Scrollable>(null);

  React.useEffect(() => {
    if (Platform.OS !== 'web' || !isDesktop || !ref.current) {
      return;
    }
    // Resolve the underlying DOM element on react-native-web (React 19 safe)
    const slider = (ref.current?.getScrollableNode?.() || null) as HTMLDivElement | null;

    if (!slider) {
      return;
    }

    let isDragging = false;
    let isMouseDown = false;
    let startX = 0;
    let scrollLeft = 0;

    const previousCursor = slider.style.cursor;
    const suppressClick = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const mouseDown = (e: MouseEvent) => {
      slider.removeEventListener('click', suppressClick, true);
      isMouseDown = true;
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;

      slider.style.cursor = cursor;
    };

    const mouseUp = () => {
      if (isDragging) {
        slider.addEventListener('click', suppressClick, { capture: true, once: true });
      }

      isMouseDown = false;
      isDragging = false;
      slider.style.cursor = 'default';
    };

    const mouseMove = (e: MouseEvent) => {
      if (!isMouseDown) {
        return;
      }

      // Require n pixels movement before start of drag (3 in this case )
      const x = e.pageX - slider.offsetLeft;
      if (Math.abs(x - startX) < 3) {
        return;
      }

      isDragging = true;
      e.preventDefault();
      const walk = x - startX;
      slider.scrollLeft = scrollLeft - walk;
    };

    slider.addEventListener('mousedown', mouseDown);
    window.addEventListener('mouseup', mouseUp);
    window.addEventListener('mousemove', mouseMove);

    return () => {
      slider.removeEventListener('click', suppressClick, true);
      slider.style.cursor = previousCursor;
      slider.removeEventListener('mousedown', mouseDown);
      window.removeEventListener('mouseup', mouseUp);
      window.removeEventListener('mousemove', mouseMove);
    };
  }, [cursor, isDesktop]);

  return React.useCallback(
    (element: Scrollable | null) => {
      ref.current = element;
      if (typeof outerRef === 'function') {
        outerRef(element);
      } else if (outerRef) {
        outerRef.current = element;
      }
    },
    [outerRef]
  );
}
